import React, { useEffect, useMemo, useState } from 'react';

import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';
import { selectLastSubProgram } from 'components/common/UploadFileModal/components/UploadMppModal/UploadMppModal.utils';
import { Details } from 'components/common/UploadFileModal/components/programLibrary.types';
import classes from 'components/common/UploadFileModal/programLibraryUploadFiles.module.scss';
import { CustomizedTooltip } from 'components/common/index';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Switch } from 'components/DesignSystem/Switch';

interface Props {
    details: Details;
    isUploading: boolean;
    handleFetchData: any;
    onClose: () => void;
    open: boolean;
}

export const UploadMppModal = ({ details: detailsFromFile, isUploading, handleFetchData, onClose, open }: Props) => {
    const { project: storedProject, contract: storedContract } = useProjectTreeStore();
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const { data: projectHierarchyList, isLoading: loadingGeneralList } =
        useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');
    const { handleSnackBar } = useCustomSnackBar();

    const onSelectionFinish = ({ project, contract }) => {
        setSelectedProject(project);
        setSelectedContract(contract);
    };

    const { control, handleSubmit, watch, setValue, reset } = useForm<Details>({
        values: {
            ...detailsFromFile,
            isBaseline: false,
            isTransformMPP: false,
        },
    });

    useEffect(() => {
        reset({
            ...detailsFromFile,
            isBaseline: false,
            isTransformMPP: false,
        });
    }, [detailsFromFile, reset]);

    const { fields } = useFieldArray({
        control,
        name: 'versions',
    });
    const details = watch();

    const selectedNode = useMemo(() => {
        return storedContract || selectLastSubProgram({ projectHierarchyList, storedProject });
    }, [storedProject, storedContract, projectHierarchyList]);

    useEffect(() => {
        selectedNode && setSelectedContract(selectedNode);
    }, [selectedNode]);

    useEffect(() => {
        storedProject && setSelectedProject(storedProject);
        storedContract && setSelectedContract(storedContract);
    }, [storedProject, storedContract]);

    const handleFinishUpload = () => {
        if (!selectedContract) {
            handleSnackBar('Sub-Program is mandatory', 'error');
        } else {
            const payload = { details, contract: selectedContract, project: selectedProject };
            handleFetchData(payload);
        }
    };

    const handleIsBaseline = (e: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
        setValue('isBaseline', e.target.checked);
        fields.forEach((_, index) => {
            setValue(`versions.${index}.isBaseline`, value, { shouldDirty: true });
        });
    };
    return (
        <Modal
            open={open}
            onClose={onClose}
            showCloseButton={!isUploading}
            actions={
                <>
                    <Button size={'medium'} variant={'secondary'} disabled={isUploading} onClick={onClose}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'medium'}
                        variant={'primary'}
                        disabled={isUploading}
                        isLoading={isUploading}
                        onClick={handleSubmit(handleFinishUpload)}
                    >
                        Upload
                    </Button>
                </>
            }
            title={'Upload MPP File'}
        >
            <form>
                <div className={classes.ProgramLibraryUploadFilesContainer}>
                    <div className={classes.uploadFilesHeader}>
                        <div className={classes.uploadFilesHeaderSection}>
                            {loadingGeneralList ? (
                                <div>Loading</div>
                            ) : (
                                selectedContract?.uuid && (
                                    <TreeCrumbs
                                        selectedNode={selectedContract.uuid}
                                        onSelectionFinish={onSelectionFinish}
                                        data={projectHierarchyList}
                                        showAddNewButton={false}
                                        showAll={false}
                                    />
                                )
                            )}
                        </div>
                        <div className={classes.uploadFilesHeaderSection}>
                            <span>Source File:</span>
                            <span className={classes.uploadFilesHeaderName}>
                                <CustomizedTooltip
                                    tooltipPosition={TooltipPosition.Center}
                                    triggerElement={<div className={classes.ellipsis}>{details.file?.name}</div>}
                                    tooltipContent={details.file?.name}
                                />
                            </span>
                        </div>
                    </div>

                    <div className={classes.content}>
                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        onChange={handleIsBaseline}
                                        label={'Is Baseline / Re-baseline'}
                                        labelPlacement={'start'}
                                    />
                                )}
                                name={'isBaseline'}
                                control={control}
                            ></Controller>
                        </div>

                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        label={'Transform MPP custom columns to Activity Codes'}
                                        labelPlacement={'start'}
                                    />
                                )}
                                name={'isTransformMPP'}
                                control={control}
                            ></Controller>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
};
